import { Link } from "react-router-dom";
import { ArrowRight, Clock, Users, Home, Laptop, Printer, ShieldCheck, AlertTriangle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SectionLabel } from "@/components/site/FormBits";
import { useAuth } from "@/context/AuthContext";
import { useSeo, pageSeo } from "@/lib/seo";
import { CURRICULUM } from "@/lib/constants";
import { SampleCertificate } from "@/pages/CertificatePage";
import { CourseFeatures } from "@/components/site/CourseFeatures";
import { ShareCourse } from "@/components/site/ShareArticle";
import { BeginnerPricing } from "@/components/site/BeginnerPricing";
import { BookCallButton } from "@/components/site/BookCall";
import { RetreatDates } from "@/components/site/RetreatDates";
import { RetreatBundle } from "@/components/avenues/AvenueParts";

const FACTS = [
  { icon: Users, t: "Built for explorers", d: "For people exploring remote work, freelance services, online business, or location-flexible work." },
  { icon: Home, t: "In person at Esc, every day", d: "Each day is 2 hours of teaching with Greg at Esc Digital Nomad Village. The afternoon is yours: optional, self-guided practice on your worksheets in the cowork." },
  { icon: Laptop, t: "Digital worksheets", d: "Sign in to complete each worksheet online, save your progress, and come back any time." },
  { icon: Printer, t: "Print for class", d: "Print a day's worksheet, every participant worksheet, or a blank copy on US Letter paper." },
  { icon: ShieldCheck, t: "Apply first", d: "Students apply and wait for host approval before lessons unlock." },
];

const MAKES = [
  ["Know your Freedom Number", "Your Break-even and the monthly number new income must beat before it's safe to leave your job."],
  ["Test real income avenues", "A tour of 11 ways to earn online, then a real 90-minute test of your top two."],
  ["Show credible proof", "Real success stories and creations from Greg's own digital nomad journey, to open your mind to paths and opportunities you may not have considered — then you build the proof that fits the path you choose."],
  ["Understand the money", "A monthly budget at home and in Nicaragua, your runway, and a multi-currency account."],
  ["Make one grounded decision", "An Avenue Scorecard, a 90-day plan with continue / revise / delay / stop gates, and an accountability partner."],
];

const DETAILS = [
  ["Course dates", "Pilot: Sun 15 – Mon 23 Nov 2026 · Group 2: Sun 29 Nov – Mon 7 Dec 2026 · Group 3: Sun 24 Jan – Mon 1 Feb 2027 · Group 4: Sun 7 – Mon 15 Feb 2027"],
  ["Tuition", "Retreat twin room $1,190 · Retreat private room $1,470"],
  ["Stay the month", "Twin room $3,295 · Private room $3,750 (reserve on a free call with Greg)"],
  ["Format", "In person only: 7-day retreat in Popoyo, Nicaragua (8 nights, Sun–Mon)"],
  ["Group size", "Max 10 people per retreat (pilot group 5)"],
  ["Retreat venue", "Esc Digital Nomad Village, Popoyo, Nicaragua"],
  ["Retreat includes", "8 nights in a private or twin room, breakfast, lunch and dinner, the course with Greg, Esc membership, 1 group beginner surf lesson (board included), daily yoga and gym access"],
  ["Flights & travel insurance", "Not included"],
];

const SEO = pageSeo("/digital-nomad-beginners-course");

function ApplyCta({ testId, className = "" }) {
  const { user } = useAuth();
  const to = user ? (user.role === "host" ? "/host" : "/apply") : "/signup?next=/apply";
  return (
    <Link to={to} className={`inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-7 py-4 font-semibold shadow-md hover:bg-[#D96F28] hover:-translate-y-0.5 transition-transform ${className}`} data-testid={testId}>
      Apply for the Course <ArrowRight className="h-4 w-4" />
    </Link>
  );
}

export default function CoursePage() {
  useSeo(SEO);
  return (
    <article>
      <header className="relative bg-[#0B2A4A] text-[#F5ECD7] grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-end">
          <div>
            <p className="rise font-mono-ui text-xs tracking-[0.3em] text-[#F08A3C]">DIGITAL NOMAD NICARAGUA · HOST-LED COURSE</p>
            <h1 className="rise rise-1 mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-white">Become a Digital Nomad for Beginners</h1>
            <h2 className="rise rise-2 mt-6 text-base md:text-lg font-normal text-[#F5ECD7]/85 max-w-2xl leading-relaxed">
              Seven days, taught in person as a 7-day retreat at Esc Digital Nomad Village in Popoyo, Nicaragua. Test a realistic remote-work route — remote role, freelance service, or small online business — and leave with an evidence-based next-step plan.
            </h2>
            <div className="rise rise-3 mt-8 flex flex-wrap items-center gap-3"><ApplyCta testId="course-apply-btn" /><BookCallButton source="beginners-hero" light testId="course-book-call-btn" /><ShareCourse path="/digital-nomad-beginners-course" title="Become a Digital Nomad for Beginners — 7-day course in Nicaragua" /></div>
          </div>
          <img src="/brand/logo-square.jpg" alt="Digital Nomad Nicaragua logo" className="hidden lg:block justify-self-end w-64 rounded-3xl bg-white p-3" />
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <SectionLabel>How the course works</SectionLabel>
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">A practical remote work course, not a beach fantasy</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FACTS.map(({ icon: Icon, t, d }) => (
            <div key={t} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6 hover:shadow-md transition-shadow">
              <Icon className="h-6 w-6 text-[#1A9AA8]" />
              <h3 className="mt-4 font-semibold text-[#0B2A4A]">{t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-6" data-testid="course-formats">
        <SectionLabel>Where the course happens</SectionLabel>
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">In person only, at Esc Digital Nomad Village in Popoyo</h2>
        <div className="mt-8 grid gap-5">
          <div className="rounded-2xl overflow-hidden border border-[#0B2A4A]/10 bg-[#0B2A4A] text-[#F5ECD7] grid sm:grid-cols-[1fr_1.1fr]" data-testid="format-retreat">
            <img src="/brand/esc-clubhouse.jpg" alt="Esc Digital Nomad Village clubhouse at dusk in Popoyo, Nicaragua" className="h-48 sm:h-full w-full object-cover" />
            <div className="p-7">
              <h3 className="text-xl font-semibold text-white">Retreat · 7 days in Popoyo · from $1,190</h3>
              <p className="mt-2 text-sm text-[#F5ECD7]/80 leading-relaxed">A seven-day retreat at Esc Digital Nomad Village on the Pacific coast in Popoyo. Daily in-person lessons, practice labs, and printed worksheets — with your answers saved online too.</p>
            </div>
          </div>
        </div>
      </section>

      <BeginnerPricing />
      <RetreatBundle testId="course-avenues-bundle" />
      <RetreatDates />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10 grid lg:grid-cols-[1fr_1.5fr] gap-12" data-testid="course-curriculum">
        <div>
          <SectionLabel>Seven-day curriculum</SectionLabel>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">What you'll work through, day by day</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">Each day follows the same rhythm: morning wellness, a 3-hour workshop touring real income avenues, an afternoon hands-on lab, and an evening together. Every day ends with a worksheet saved to your private profile.</p>
        </div>
        <Accordion type="single" collapsible defaultValue="d1" className="border-t border-[#0B2A4A]/10">
          {CURRICULUM.map((c) => (
            <AccordionItem key={c.day} value={`d${c.day}`} className="border-[#0B2A4A]/10">
              <AccordionTrigger className="hover:no-underline py-5" data-testid={`curriculum-day-${c.day}`}>
                <span className="flex items-center gap-4 text-left">
                  <span className="font-mono-ui text-sm font-bold text-[#F08A3C] w-14 shrink-0">DAY {c.day}</span>
                  <h3 className="font-display text-lg font-semibold text-[#0B2A4A]">{c.title}</h3>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pl-[4.5rem] text-slate-600 leading-relaxed">
                <p>{c.purpose}</p>
                <p className="mt-3 text-sm"><strong className="text-[#0B2A4A]">You'll make:</strong> {c.result}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid lg:grid-cols-2 gap-10">
        <div className="rounded-3xl bg-[#F5ECD7] p-8 sm:p-10">
          <SectionLabel>Learn from real experience</SectionLabel>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-[#0B2A4A]">Taught by Mr. Greg (Greg Miceli)</h2>
          <p className="mt-4 text-slate-700 leading-relaxed">
            Lessons draw on hands-on work building websites and digital products, marketing and SEO, online business systems, client work, and operating businesses from Nicaragua — including examples such as K9OS, TrackMyPackage, and Ship Costa Rica.
          </p>
          <p className="mt-4 text-sm text-slate-600 leading-relaxed">These are presented as examples you can learn from, with the tradeoffs included. Business revenue is not profit, and the host's results are not a promise of yours.</p>
        </div>
        <div>
          <SectionLabel>What participants make</SectionLabel>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-[#0B2A4A]">Evidence, not hype</h2>
          <dl className="mt-6 flex flex-col divide-y divide-[#0B2A4A]/10">
            {MAKES.map(([t, d]) => (
              <div key={t} className="py-4 grid sm:grid-cols-[200px_1fr] gap-2">
                <dt className="font-semibold text-[#0B2A4A]">{t}</dt>
                <dd className="text-sm text-slate-600">{d}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10 grid lg:grid-cols-2 gap-10">
        <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-8" data-testid="course-details-placeholders">
          <h2 className="text-xl font-bold text-[#0B2A4A]">Course details</h2>
          <dl className="mt-5 flex flex-col gap-3 text-sm">
            {DETAILS.map(([k, v]) => (
              <div key={k} className="flex flex-col sm:flex-row sm:justify-between gap-1 border-b border-dashed border-[#0B2A4A]/15 pb-3">
                <dt className="font-semibold text-[#0B2A4A]">{k}</dt>
                <dd className={v.startsWith("[") ? "text-slate-500 italic" : k === "Tuition" ? "font-semibold text-[#D96F28] sm:text-right" : "text-slate-700 sm:text-right"}>{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="rounded-2xl border border-[#F08A3C]/40 bg-[#F08A3C]/5 p-8" data-testid="course-disclaimer">
          <h2 className="text-xl font-bold text-[#0B2A4A] flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[#F08A3C]" /> Honest expectations</h2>
          <ul className="mt-4 text-sm text-slate-700 space-y-2 list-disc pl-5 leading-relaxed">
            <li>This course does not promise a job, income, visa approval, or travel success.</li>
            <li>It helps you test a realistic remote-work route and make an evidence-based next-step plan.</li>
            <li>Staying in your current job while you test a remote path is a valid — often smart — result.</li>
            <li>Verify legal, tax, health, insurance, employment, and destination questions with current official sources or qualified professionals.</li>
          </ul>
        </div>
      </section>

      <CourseFeatures testId="nomad-course-features" inPerson />

      <SampleCertificate course="Become a Digital Nomad for Beginners" how="Your certificate is issued automatically only when all 7 days are marked complete — every lesson, worksheet and homework reflection done." />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <div className="rounded-3xl bg-[#0B2A4A] text-[#F5ECD7] p-8 sm:p-12 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Ready to test your remote-work route?</h2>
            <p className="mt-3 text-[#F5ECD7]/80 max-w-xl">Create your account, complete a short application, and you'll see your status right away. Lessons unlock after Mr. Greg approves your application.</p>
          </div>
          <ApplyCta testId="course-apply-btn-bottom" className="shrink-0" />
        </div>
      </section>
    </article>
  );
}
